import React from 'react';
import PropTypes from 'prop-types';
import {
  cloneObject,
  getStorableDateString,
  manageEventListener,
  validatePropTypeDate
} from '../../../common/utils';
import withLocalization from '../../higherOrderComponents/withLocalization';
import { allowedMessageProps } from '../../lists/SystemMessages';
import { Button } from '../ActionElement';
import { CustomDayPicker } from '../CustomDayPicker';
import InputFieldDefault from './_InputFieldDefault';

const completeYear = new RegExp('\\d{4}$');
const completeYearMonth = new RegExp('(\\d{4})-(\\d{2})$');
const completeYearMonthDay = new RegExp('(\\d{4})-(\\d{2})-(\\d{2})$');

class InputFieldDate extends React.Component {
  constructor(props) {
    super(props);

    const {
      inputAttributes,
      dateDefault = inputAttributes.value ? new Date(inputAttributes.value) : new Date(),
      isDatePickerOpen = false
    } = props;

    this.state = {
      isDatePickerOpen,
      blockOpeningOnFocus: false,
      inputValue: '',
      inputYear: dateDefault.getFullYear(),
      inputMonth: dateDefault.getMonth(),
      inputDay: dateDefault.getDay()
    };

    this.datePickerWrapperRef = React.createRef();
    this.toggleDatePicker = this.toggleDatePicker.bind(this);
    this.handleDayClick = this.handleDayClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.updateState = this.updateState.bind(this);
    this.controlDatePickerFocus = this.controlDatePickerFocus.bind(this);
    this.handleKeyUpEvent = this.handleKeyUpEvent.bind(this);
    this.manageEventListeners = this.manageEventListeners.bind(this);
  }

  componentDidMount() {
    const {
      props: {
        inputAttributes: {
          name,
          value
        },
        setFieldValue
      },
      manageEventListeners
    } = this;

    if (value && value !== '' && !completeYearMonthDay.test(value)) {
      setFieldValue(name, getStorableDateString(new Date(value)));
      console.error(
        'WARNING: form was set dirty by InputFieldDate because it was not formatted correctly. '
        + 'Please make sure you format the date when passing initialValues to the FormikForm component.'
      );
    }

    manageEventListeners('add');
  }

  componentWillUnmount() {
    this.manageEventListeners('remove');
  }

  toggleDatePicker(shouldDatePickerOpen) {
    this.setState(({ isDatePickerOpen }) => ({
      isDatePickerOpen: shouldDatePickerOpen !== undefined
        ? shouldDatePickerOpen
        : !isDatePickerOpen
    }));
  }

  handleDayClick(pickedDay, { disabled }, resolve) {
    const callResolver = function () {
      if (resolve) {
        resolve();
      }
    };

    if (!disabled) {
      const {
        props: {
          inputAttributes: {
            name
          },
          customChangeHandler,
          setFieldValue
        }
      } = this;

      const value = getStorableDateString(pickedDay);

      if (customChangeHandler) {
        customChangeHandler({ value });
      }
      else {
        setFieldValue(name, value);
      }

      this.setState({
        inputValue: value,
        isDatePickerOpen: false,
        blockOpeningOnFocus: true
      }, callResolver);
    }

    callResolver();
  }

  handleInputChange(formikChangeHandler, e) {
    const {
      props: {
        customChangeHandler
      },
      state: {
        inputValue: prevValue
      },
      toggleDatePicker
    } = this;

    const validInputRegex = new RegExp(
      '(((1|$)(9|$)([0-9]|$)([0-9]|$))|(2|$)(0|$)([0-9]|$)([0-9]|$))'
      + '(-|$)(((0|$)([1-9]|$))|(1|$)([0-2]|$))(-|$)'
      + '((0|$)([1-9]|$)|([1-2]|$)([0-9]|$)|(3|$)([0-1]|$))',
      'y'
    );

    const prevValueLen = prevValue.length;
    let inputValue = e.target.value;
    const inputValueLen = inputValue.length;

    if (validInputRegex.test(inputValue) && inputValueLen < 11) {
      const isInputDeleted = inputValueLen < prevValueLen;
      const isValueFullYear = completeYear.test(inputValue);
      const isValueYearDashMonth = completeYearMonth.test(inputValue);
      const isValueFullDate = completeYearMonthDay.test(inputValue);

      if (!isInputDeleted) {
        if (isValueYearDashMonth || isValueFullYear) {
          inputValue += '-';
        }

        if (isValueFullDate) {
          const inputDay = parseInt(inputValue.slice(inputValueLen - 2, inputValueLen), 10);
          this.setState({
            inputDay,
            isDatePickerOpen: false,
            blockOpeningOnFocus: true
          });
        }

        else if (isValueYearDashMonth) {
          const inputMonth = parseInt(inputValue.slice(inputValueLen - 2, inputValueLen), 10) - 1;
          this.setState({
            inputMonth,
            blockOpeningOnFocus: false
          });
        }

        else if (isValueFullYear) {
          const inputYear = parseInt(inputValue.slice(0, inputValueLen), 10);
          this.setState({
            inputYear,
            blockOpeningOnFocus: false
          });
        }
      }

      else {
        toggleDatePicker(true);
      }

      const prevLastCharIndex = prevValueLen - 1;
      if (isInputDeleted && prevValue[prevLastCharIndex] === '-') {
        inputValue = inputValue.slice(0, prevLastCharIndex - 1);
      }

      e.target.value = inputValue;

      if (customChangeHandler) {
        customChangeHandler({ inputValue });
      }
      else {
        formikChangeHandler(e);
      }

      this.setState({ inputValue });
    }
  }

  updateState(newState, callback) {
    this.setState(newState, callback);
  }

  controlDatePickerFocus() {
    setTimeout(() => { // TODO:
      const {
        datePickerWrapperRef,
        toggleDatePicker
      } = this;

      const { current } = datePickerWrapperRef;

      if (current && !current.contains(document.activeElement)) {
        toggleDatePicker(false);
      }
    }, 0);
  }

  handleKeyUpEvent(e) {
    e.preventDefault();
    const {
      state: {
        isDatePickerOpen
      },
      toggleDatePicker
    } = this;

    const isInputFocused = document.activeElement.tagName === 'INPUT';

    switch (e.key) {
      case 'Escape':
        if (isDatePickerOpen) {
          e.stopPropagation();
          toggleDatePicker(false);
        }
        break;

      case 'ArrowUp':
        if (isInputFocused) {
          toggleDatePicker(false);
        }
        break;

      case 'ArrowDown':
        if (isInputFocused) {
          toggleDatePicker(true);
        }
        break;

      default:
        return undefined;
    }
  }

  handleInputFocus() {
    const {
      state: {
        blockOpeningOnFocus
      },
      toggleDatePicker
    } = this;

    if (!blockOpeningOnFocus) {
      toggleDatePicker(true);
    }
  }

  handleInputBlur() {
    this.setState({ blockOpeningOnFocus: false });
  }

  manageEventListeners(action) {
    const {
      datePickerWrapperRef: {
        current: datePickerWrapper
      },
      controlDatePickerFocus,
      handleKeyUpEvent
    } = this;

    const events = [
      { event: 'focusout', func: controlDatePickerFocus },
      { event: 'keyup', func: handleKeyUpEvent }
    ];

    events.forEach(({ event, func }) => manageEventListener(datePickerWrapper, event, action, func));
  }

  render() {
    const {
      props,
      state: {
        isDatePickerOpen,
        inputYear,
        inputMonth,
        inputDay
      },
      datePickerWrapperRef,
      toggleDatePicker,
      handleDayClick,
      handleInputChange,
      handleInputFocus,
      handleInputBlur,
      updateState
    } = this;

    const {
      inputAttributes,
      dateDefault,
      dateMin,
      dateMax,
      onDayClick,
      translate
    } = props;

    const {
      type,
      value,
      onChange,
      disabled: inputDisabled
    } = inputAttributes;

    const modifiers = { disabled: day => inputDisabled || day < dateMin || day > dateMax };

    if (value && value !== '') {
      const dateValue = new Date(value);
      modifiers.selected = day => day.setHours(0, 0, 0) === dateValue.setHours(0, 0, 0);
    }

    inputAttributes.onChange = e => {
      handleInputChange(onChange, e);
    };

    const excludedProps = ['isDatePickerOpen'];
    const inputFieldProps = cloneObject(props, {}, excludedProps);
    const defaultDayClickHandler = (pickedDay, pickedDayParams) => handleDayClick(pickedDay, pickedDayParams);
    const passedDayClickHandler = function (pickedDay, pickedDayParams) {
      onDayClick(() => new Promise((resolve => {
        handleDayClick(pickedDay, pickedDayParams, resolve);
      })));
    };

    if (type !== 'date-picker') {
      inputFieldProps.inputAttributes = {
        ...inputAttributes,
        type: 'text',
        placeholder: 'YYYY-MM-DD',
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        autoComplete: 'off'
      };

      const datePickerWrapClasses = ['date-picker-wrap'];

      if (isDatePickerOpen) {
        datePickerWrapClasses.push('open');
      }

      return (
        <div className='date-input-wrap' ref={datePickerWrapperRef}>
          <div className='buttoned-input-wrap'>
            <InputFieldDefault {...inputFieldProps} />
            <Button
              appearance='buttonWrap'
              onClick={() => toggleDatePicker()}
              title={translate('input-date-button-dropdown-title')}
              aria-expanded={isDatePickerOpen}
              iconName={`chevron-${isDatePickerOpen ? 'up' : 'down'}`}
              disabled={inputDisabled || false}
            />
          </div>

          <div className={datePickerWrapClasses.join(' ')}>
            <CustomDayPicker
              onDayClick={onDayClick ? passedDayClickHandler : defaultDayClickHandler}
              updateDateInputState={updateState}
              year={inputYear}
              month={inputMonth}
              day={inputDay}
              modifiers={modifiers}
              defaultDate={dateDefault}
              defaultDateMin={dateMin}
              defaultDateMax={dateMax}
            />
          </div>
        </div>
      );
    }

    inputFieldProps.inputAttributes = {
      ...inputAttributes,
      type: 'hidden'
    };

    return (
      <div className='date-picker-input-wrap' ref={datePickerWrapperRef}>
        <CustomDayPicker
          onDayClick={onDayClick ? passedDayClickHandler : defaultDayClickHandler}
          updateDateInputState={updateState}
          year={inputYear}
          month={inputMonth}
          day={inputDay}
          modifiers={modifiers}
          defaultDate={dateDefault}
          defaultDateMin={dateMin}
          defaultDateMax={dateMax}
        />

        <InputFieldDefault {...inputFieldProps} />
      </div>
    );
  }
}

InputFieldDate.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  isDatePickerOpen: PropTypes.bool,
  dateDefault: (props, propName, componentName) => validatePropTypeDate(props, propName, componentName),
  dateMin: (props, propName, componentName) => validatePropTypeDate(props, propName, componentName),
  dateMax: (props, propName, componentName) => validatePropTypeDate(props, propName, componentName),
  inputAttributes: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool
    ]).isRequired
  }).isRequired,
  description: PropTypes.string.isRequired,
  message: PropTypes.shape(allowedMessageProps),
  onDayClick: PropTypes.func,
  customChangeHandler: PropTypes.func
};

export default withLocalization(InputFieldDate, 'elements/customInput');